import Head from "next/head";
import { useRouter } from "next/router";
import { useContext } from "react";

import { AppContext, ThemeMode } from "@context/context";

export interface PageMetadataProps {
  title: string;
  description: string;
  blogPostDate?: string;
}

export const PageMetadata = ({
  title,
  description,
  blogPostDate,
}: PageMetadataProps) => {
  const appContext = useContext(AppContext);
  const { locale, asPath } = useRouter();

  const baseUrl = "https://bluesbrackets.com";
  const cleanPath = asPath.split(/[?#]/)[0];
  const canonicalUrl =
    locale === "pl" ? `${baseUrl}/pl${cleanPath}` : `${baseUrl}${cleanPath}`;

  const structuredDataForBlogs = blogPostDate
    ? {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": title,
        description,
        "url": canonicalUrl,
        "mainEntityOfPage": canonicalUrl,
        "datePublished": blogPostDate,
        "author": {
          "@type": "Organization",
          "name": "Blues Brackets",
        },
        "publisher": {
          "@type": "Organization",
          "name": "Blues Brackets",
          "logo": {
            "@type": "ImageObject",
            "url": "https://bluesbrackets.com/favicon-dark.png",
          },
        },
      }
    : null;

  return (
    <Head>
      <title>bluesBrackets - {title}</title>
      <meta name="description" content={description} />
      <meta name="viewport" content="initial-scale=1, width=device-width" />
      <link rel="canonical" href={canonicalUrl} />

      <link rel="alternate" hrefLang="en" href={`${baseUrl}${cleanPath}`} />
      <link rel="alternate" hrefLang="pl" href={`${baseUrl}/pl${cleanPath}`} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

      <meta property="og:title" content={`bluesBrackets - ${title}`} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={blogPostDate ? "article" : "website"} />
      <meta
        property="og:image"
        content="https://bluesbrackets.com/og-dafault.png"
      />

      {structuredDataForBlogs && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({ ...structuredDataForBlogs }),
          }}
        />
      )}

      <link
        rel="icon"
        type="image/svg+xml"
        href={
          appContext.systemTheme === ThemeMode.Light
            ? "/favicon-light.svg"
            : "/favicon-dark.svg"
        }
      />
      <link
        rel="icon"
        type="image/png"
        href={
          appContext.systemTheme === ThemeMode.Light
            ? "favicon-light.png"
            : "favicon-dark.png"
        }
      />
      <link
        rel="apple-touch-icon"
        sizes="180x180"
        href="/apple-touch-icon.png"
      />
      <link
        rel="icon"
        type="image/png"
        sizes="32x32"
        href="/favicon-32x32.png"
      />
      <link
        rel="icon"
        type="image/png"
        sizes="16x16"
        href="/favicon-16x16.png"
      />
      <link rel="manifest" href="/site.webmanifest" />
      <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#1a1a1a" />
      <meta name="msapplication-TileColor" content="#1a1a1a" />
      <meta name="theme-color" content="#1a1a1a" />
    </Head>
  );
};
